import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Order } from "../../../../core/models/entities/Order";
import { Dialog_VM, DialogFrame } from "../../../components/Dialogs/dialog-base";
import { useConfirmationDialog } from "../../../components/Dialogs/confirmation-dialog";
import { useRepositoriesContext } from "../../../../contexts/RepositoriesContext";
import { PrimaryButton } from "../../../components/Buttons/PrimaryButton";
import { SecondaryButton } from "../../../components/Buttons/SecondaryButton";
import { BadgeChip } from "../../../components/OrgShell/BadgeChip";
import {
  showErrorToast,
  showInfoToast,
  showSuccessToast,
} from "../../../components/Toast/Toast";
import { formatMoney } from "../../admin-api-keys/shared/formatters";
import { formatDate } from "../../../../utils/functions/DateUtil";
import { parseUtcStamp } from "../../../../utils/functions/storageUpdatedAt";
import {
  OrderPriceAdjustmentDto,
  OrderPriceAdjustmentHistoryDto,
  OrderPriceAdjustmentLineDto,
  OrderPriceAdjustmentResultDto,
} from "../../../../data/models/responses/OrderPriceAdjustmentDto";
import { VALIDATION_ERRORS } from "../../../../data/models/mappers/mapPriceAdjustmentError";
import {
  PriceDraft,
  computeTotals,
  effectivePrice,
  evaluateDraft,
  formatPriceInput,
  hasAdjustableAdditionalFee,
  initialDraft,
  notAdjustableKey,
  parsePriceInput,
  round2,
} from "./adjustPrices";

export const NOTE_MAX_LENGTH = 1000;

interface AdjustPricesDialogProps {
  dialogVm: Dialog_VM;
  // The row the admin clicked; null while nothing is being adjusted.
  order: Order | null;
  onClose: () => void;
}

// The agreed-price field: right-aligned figures (tabular) with the currency
// as a suffix inside the field (pr-12 leaves room for it), full width of
// its cell on a phone, a fixed 9rem on wider screens.
// The field fills its wrapper (w-full below sm, sm:w-36 from sm), which
// may shrink inside the fixed-layout column (min-w-0) - so the field never
// spills into "Our price" when the "Agreed" chip sits beside it.
const inputClass =
  "w-full rounded border border-[#C3CAD9] bg-white py-1.5 pl-2 pr-12 text-right text-sm tabular-nums " +
  "focus:outline-none focus:ring-2 focus:ring-[#3CB44A] disabled:bg-[#FAFBFE] disabled:text-[#8A93A6]";
const invalidInputClass = " !border-red-500 !ring-red-300";
// An edit that is not saved yet (differs from the stored agreement) - the
// same orange as the changed totals in the Before -> After card.
const changedInputClass = " !border-orange-500 !ring-orange-300";
// The field shows a stored agreement unchanged.
const agreedInputClass = " font-semibold";

// The dialog must fit a phone (<= 400 px) without horizontal scrolling:
// ModalBox is min-w-[400px]/w-fit by default, so the box itself is sized
// here (opt-in prop on DialogFrame; every other dialog keeps its box).
const dialogBoxClass = "!min-w-0 !w-[min(960px,calc(100vw-16px))]";

// Responsive table cells: a real table from the sm breakpoint (640 px) and
// a stacked card per line below it (each cell = "label  value" on one
// line), so nothing ever scrolls sideways - one DOM for both, so the
// test ids and the inputs exist exactly once. Both tables are table-fixed:
// with the default auto layout a nowrap (truncate) product name or e-mail
// sets the table's min-content width, so a long name widened the table
// past its box (a sideways scrollbar on a 375 px phone, clipped Qty /
// currency / line totals) and the ellipsis never kicked in. Fixed layout
// keeps the table at the box width (the header widths below size the
// number columns, the first column takes the rest; below sm the header is
// hidden and the stacked rows get the whole width) and lets the truncate
// wrappers (min-w-0 / overflow-hidden) do their job.
const cellClass =
  "flex items-center justify-between gap-3 py-1 sm:table-cell sm:px-3 sm:py-2";
const cellLabelClass =
  "text-[11px] font-semibold uppercase tracking-wide text-gray-500 sm:hidden";
const headClass =
  "px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500";

// Admin "Adjust prices" for an "Awaiting payment" order (flag
// REACT_APP_ADMIN_PRICE_ADJUSTMENT_ENABLED). 1 GET at open, 1 PUT at save -
// no NAV call, no price lookup. "Our price" stays as documentation; the
// agreed price per line is what the new Stripe link, the mail and (at
// payment) NAV get. Totals are previewed with the same formula the API uses
// (adjustPrices.ts); the API's answer is the fact.
export function AdjustPricesDialog({
  dialogVm,
  order,
  onClose,
}: AdjustPricesDialogProps): React.ReactElement | null {
  const { t } = useTranslation();
  const { orderRepository } = useRepositoriesContext();
  const query = orderRepository.useOrderPriceAdjustment(
    order?.id,
    dialogVm.isOpen && !!order,
  );
  const data = query.data;

  const [draft, setDraft] = useState<PriceDraft>({});
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  // A PUT whose answer never arrived (transport failure). The API may have
  // committed it - new link and mail included - so the next 409 from this
  // dialog is most likely our own save, not another admin's.
  const [saveUnconfirmed, setSaveUnconfirmed] = useState(false);

  // Fresh data (open, refetch after a version conflict) resets the draft to
  // the stored agreed prices - the version inside is the PUT's ETag.
  useEffect(() => {
    if (!data) return;
    setDraft(initialDraft(data.lines));
    setNote("");
    setSaveUnconfirmed(false);
  }, [data]);

  const evaluation = useMemo(
    () => (data ? evaluateDraft(data.lines, draft) : undefined),
    [data, draft],
  );
  const after = useMemo(
    () =>
      data && evaluation
        ? computeTotals(evaluation.effectiveLines, data.vatRate)
        : undefined,
    [data, evaluation],
  );

  // A read in flight counts as loading even when stale data is on screen:
  // after a save the query is marked stale without a refetch (repository,
  // FAST REGEL), so a re-open of the same order first shows the OLD prices
  // and the OLD version until the fresh GET lands - a save in that window
  // would only earn a 409. Same window during the refetch after a version
  // conflict. So fields and Save wait for the fresh data.
  const fetching = query.isFetching;
  const loading = query.isLoading || fetching;
  const busy = saving || fetching;

  const canSave =
    !!data &&
    data.adjustable &&
    !!evaluation &&
    evaluation.hasChange &&
    evaluation.isValid &&
    !busy;

  function close() {
    // Reset so a re-open of the same order (same cached data reference, so
    // the effect above would not fire) starts from the stored prices.
    setDraft(data ? initialDraft(data.lines) : {});
    setNote("");
    setSaveUnconfirmed(false);
    dialogVm.closeDialog();
    onClose();
  }

  // The frame's X button closes the frame itself (closeDialog, then onClose)
  // before this component gets a say, so it is guarded here - like the
  // Cancel button - while a PUT is in flight: closing mid-save would set
  // order to null under the answer (toasts after the dialog is gone, and a
  // 409's refetch would hit the now-empty key = one wasted GET).
  const frameVm: Dialog_VM = {
    ...dialogVm,
    closeDialog: () => {
      if (!saving) dialogVm.closeDialog();
    },
  };
  function closeFromFrame(): void {
    if (!saving) close();
  }

  const confirm = useConfirmationDialog(
    t("orderPriceAdjustment.confirm", {
      orderNumber: data?.orderNumber ?? order?.orderNumber ?? "",
      total: after ? formatMoney(round2(after.total), data?.currency) : "",
    }),
    () => void save(),
  );

  async function save(): Promise<void> {
    if (!data || !evaluation || !canSave) return;
    setSaving(true);
    let result: OrderPriceAdjustmentResultDto;
    try {
      result = await orderRepository.adjustOrderPrices(data.orderId, {
        expectedVersion: data.version,
        note: note.trim() === "" ? undefined : note.trim(),
        lines: evaluation.changes,
      });
    } catch {
      // Transport failure / unknown answer: the PUT may or may not have been
      // committed (a timeout after the API's commit means the new link and
      // mail are already out). Keep the dialog and the typed prices open,
      // tell the admin to reload before trying again, and remember it so a
      // 409 on the next attempt is not blamed on "someone else".
      setSaveUnconfirmed(true);
      showErrorToast(t("orderPriceAdjustment.saveFailed"));
      setSaving(false);
      return;
    }
    setSaving(false);
    handleResult(result);
  }

  function handleResult(result: OrderPriceAdjustmentResultDto): void {
    const orderNumber = data?.orderNumber ?? "";
    switch (result.outcome) {
      case "adjusted":
        showSuccessToast(t("orderPriceAdjustment.success", { orderNumber }));
        if (result.oldLinkKilled === false) {
          showInfoToast(t("orderPriceAdjustment.oldLinkNotKilled"));
        }
        if (result.mailSent === false) {
          showInfoToast(t("orderPriceAdjustment.mailNotSent"));
        }
        close();
        return;
      case "versionConflict":
        // The order moved on since our GET: reload (the effect above resets
        // the draft to the stored prices) and let the admin look again.
        // After an unconfirmed save of our own the version bump is most
        // likely that save - say "reloaded", not "changed by someone else".
        showInfoToast(
          t(
            saveUnconfirmed
              ? "orderPriceAdjustment.reloadedAfterUnconfirmedSave"
              : "orderPriceAdjustment.versionConflict",
          ),
        );
        void query.refetch();
        return;
      case "notAdjustable":
        showErrorToast(
          t("orderPriceAdjustment.notAdjustable", {
            orderNumber,
            reason: reasonText(result.reason ?? result.currentStatus ?? ""),
          }),
        );
        close();
        return;
      case "paidOnOldLink":
        showErrorToast(t("orderPriceAdjustment.paidOnOldLink", { orderNumber }));
        close();
        return;
      case "stripeUnavailable":
        showErrorToast(t("orderPriceAdjustment.stripeUnavailable"));
        return;
      case "validationError":
        if (result.error === "noChange") {
          showInfoToast(t("orderPriceAdjustment.noChange"));
        } else {
          // stripeRejected carries Stripe's own message in `detail` (e.g.
          // the amount is below the currency minimum): shown after the
          // translated code, so the admin sees what to change. The dialog
          // stays open with the typed prices - nothing was written.
          const text = errorText(result.error ?? "");
          showErrorToast(
            t("orderPriceAdjustment.validationError", {
              error: result.detail ? `${text}: ${result.detail}` : text,
            }),
          );
        }
        return;
    }
  }

  // The API's 400 codes are data; the known ones render translated, an
  // unknown code (or a plain message from a newer API) renders as-is.
  function errorText(error: string): string {
    return VALIDATION_ERRORS.includes(error)
      ? t(`orderPriceAdjustment.error.${error}`)
      : error;
  }

  // The API's reason codes are data; only the rendering is translated, and
  // an unknown code renders as-is.
  function reasonText(reason: string): string {
    const known = [
      "notAwaitingPayment",
      "paymentStatus",
      "orgTypeNotSupported",
      "noStripeSession",
      "paidOnOldLink",
    ];
    return known.includes(reason)
      ? t(`orderPriceAdjustment.reason.${reason}`)
      : reason;
  }

  if (!order) return null;

  return (
    <>
      {/* The X button must go through close() too: it resets the draft, so
          an abandoned edit cannot resurface as if it were a saved agreement
          when the same order is re-opened (same cached data reference, so
          the reset effect would not fire). */}
      <DialogFrame
        dialogOption={frameVm}
        onClose={closeFromFrame}
        boxClassName={dialogBoxClass}
      >
        <div
          className={"flex w-full flex-col gap-4"}
          data-testid="adjust-prices-dialog"
        >
          <div className={"pr-8"}>
            <h2 className={"text-lg font-bold"}>
              {t("orderPriceAdjustment.title", {
                orderNumber: data?.orderNumber ?? order.orderNumber,
              })}
            </h2>
            {data && (
              <p className={"text-sm text-gray-600"}>
                {data.organizationName} · {data.status}
              </p>
            )}
          </div>
          {loading && (
            <p
              className={"text-sm text-gray-600"}
              data-testid="adjust-prices-loading"
            >
              {t("orderPriceAdjustment.loading")}
            </p>
          )}
          {query.isError && (
            <p className={"text-sm text-red-700"} data-testid="adjust-prices-error">
              {t("orderPriceAdjustment.loadFailed")}
            </p>
          )}
          {data && (
            <>
              {!data.adjustable && (
                <p
                  className={
                    "rounded border border-orange-300 bg-orange-50 px-3 py-2 text-sm text-orange-700"
                  }
                  data-testid="adjust-prices-not-adjustable"
                >
                  {t("orderPriceAdjustment.notAdjustable", {
                    orderNumber: data.orderNumber,
                    reason: reasonText(data.notAdjustableReason ?? ""),
                  })}
                </p>
              )}
              <div
                className={
                  "max-h-[45vh] overflow-y-auto rounded-lg border border-[#ECEEF9]"
                }
              >
                <table
                  className={"w-full table-fixed border-collapse text-sm"}
                  data-testid="adjust-prices-lines"
                >
                  <thead
                    className={
                      "hidden bg-[#FAFBFE] sm:table-header-group"
                    }
                  >
                    <tr>
                      <th className={`${headClass} text-left`}>
                        {t("orderPriceAdjustment.line")}
                      </th>
                      <th className={`${headClass} w-16 text-right`}>
                        {t("orderPriceAdjustment.qty")}
                      </th>
                      <th className={`${headClass} w-28 md:w-32 text-right`}>
                        {t("orderPriceAdjustment.ourPrice")}
                      </th>
                      <th className={`${headClass} w-48 md:w-56 text-right`}>
                        {t("orderPriceAdjustment.agreedPrice")}
                      </th>
                      <th className={`${headClass} w-28 md:w-32 text-right`}>
                        {t("orderPriceAdjustment.lineTotal")}
                      </th>
                    </tr>
                  </thead>
                  <tbody className={"divide-y divide-[#ECEEF9]"}>
                    {data.lines.map((line) => (
                      <PriceLineRow
                        key={line.lineId}
                        line={line}
                        currency={data.currency}
                        raw={
                          draft[line.lineId] ??
                          (line.agreedPrice === null ||
                          line.agreedPrice === undefined
                            ? ""
                            : formatPriceInput(line.agreedPrice))
                        }
                        changed={
                          !!evaluation &&
                          evaluation.changes.some((c) => c.lineId === line.lineId)
                        }
                        disabled={!data.adjustable || busy}
                        onChange={(value) =>
                          setDraft((d) => ({ ...d, [line.lineId]: value }))
                        }
                      />
                    ))}
                  </tbody>
                </table>
              </div>
              {hasAdjustableAdditionalFee(data.lines) && (
                <p className={"text-xs text-gray-600"}>
                  {t("orderPriceAdjustment.startupFeeWarning")}
                </p>
              )}
              <div
                className={
                  "grid grid-cols-1 gap-4 md:grid-cols-[minmax(0,1fr)_auto] md:items-start"
                }
              >
                <label className={"flex flex-col gap-1 text-sm"}>
                  <span className={"text-xs font-semibold text-[#8A93A6]"}>
                    {t("orderPriceAdjustment.note")}
                  </span>
                  <input
                    type="text"
                    value={note}
                    maxLength={NOTE_MAX_LENGTH}
                    disabled={!data.adjustable || busy}
                    onChange={(e) => setNote(e.target.value)}
                    className={
                      "w-full rounded border border-[#C3CAD9] bg-white px-2 py-1.5 text-sm " +
                      "focus:outline-none focus:ring-2 focus:ring-[#3CB44A] disabled:bg-[#FAFBFE]"
                    }
                    data-testid="adjust-prices-note"
                  />
                </label>
                {after && (
                  <TotalsCard
                    before={data.totals}
                    after={after}
                    currency={data.currency}
                  />
                )}
              </div>
              {data.history.length > 0 && (
                <HistoryTable history={data.history} currency={data.currency} />
              )}
            </>
          )}
          <div
            className={
              "flex flex-col-reverse gap-3 border-t border-[#ECEEF9] pt-4 sm:flex-row sm:justify-end"
            }
          >
            <SecondaryButton
              onClick={close}
              disabled={saving}
              width="fit"
              className={"w-full sm:w-auto"}
            >
              {t("cancel")}
            </SecondaryButton>
            <PrimaryButton
              onClick={confirm.open}
              disabled={!canSave}
              width="fit"
              className={"w-full px-4 sm:w-auto"}
            >
              {t("orderPriceAdjustment.save")}
            </PrimaryButton>
          </div>
        </div>
      </DialogFrame>
      {confirm.component}
    </>
  );
}

function PriceLineRow({
  line,
  currency,
  raw,
  changed,
  disabled,
  onChange,
}: {
  line: OrderPriceAdjustmentLineDto;
  currency: string;
  raw: string;
  // The field differs from the stored agreement (an unsaved edit).
  changed: boolean;
  disabled: boolean;
  onChange: (value: string) => void;
}): React.ReactElement {
  const { t } = useTranslation();
  const parsed = parsePriceInput(raw);
  const invalid = line.adjustable && !parsed.valid;
  const effective = line.adjustable
    ? effectivePrice(line.ourPrice, parsed.valid ? parsed.value : line.agreedPrice)
    : effectivePrice(line.ourPrice, line.agreedPrice);
  const lockedTitle = line.adjustable
    ? undefined
    : t(notAdjustableKey(line.productType));
  // Three states the admin must be able to tell apart at a glance: no
  // agreement (empty field, "No agreement" placeholder), a stored agreement
  // (bold value + "Agreed" chip) and an unsaved edit (orange border, no
  // chip) - so a typed-but-unsaved number never passes for an agreement.
  const hasStoredAgreement =
    line.agreedPrice !== null && line.agreedPrice !== undefined;
  const showsStoredAgreement = hasStoredAgreement && !changed && !invalid;
  const stateClass = invalid
    ? invalidInputClass
    : changed
      ? changedInputClass
      : showsStoredAgreement
        ? agreedInputClass
        : "";
  const lineTotal = round2(line.quantity * effective);
  const lineTotalChanged = lineTotal !== round2(line.lineTotal);
  return (
    <tr
      className={
        "block px-3 py-2 sm:table-row sm:p-0" +
        (line.adjustable ? "" : " text-gray-500")
      }
      title={lockedTitle}
      data-testid={`adjust-prices-line-${line.lineId}`}
    >
      <td className={`${cellClass} sm:text-left`}>
        <span className={"flex min-w-0 flex-col overflow-hidden"}>
          <span className={"flex flex-wrap items-center gap-2"}>
            <span
              className={
                "rounded bg-[#FAFBFE] px-1.5 font-mono text-[11px] text-gray-500 " +
                "ring-1 ring-inset ring-[#ECEEF9]"
              }
            >
              {line.displayNo}
            </span>
            <span className={"font-semibold"}>{line.productNo}</span>
          </span>
          <span className={"truncate text-gray-600"}>{line.productName}</span>
        </span>
      </td>
      <td className={`${cellClass} sm:text-right tabular-nums`}>
        <span className={cellLabelClass}>{t("orderPriceAdjustment.qty")}</span>
        <span>{line.quantity}</span>
      </td>
      <td className={`${cellClass} sm:text-right tabular-nums`}>
        <span className={cellLabelClass}>
          {t("orderPriceAdjustment.ourPrice")}
        </span>
        <span>{formatMoney(line.ourPrice, currency)}</span>
      </td>
      <td className={`${cellClass} sm:text-right`}>
        <span className={cellLabelClass}>
          {t("orderPriceAdjustment.agreedPrice")}
        </span>
        {line.adjustable ? (
          <span
            className={
              "flex w-full items-center justify-end gap-2 sm:w-auto"
            }
          >
            {showsStoredAgreement && (
              <BadgeChip tone="green" className={"shrink-0"}>
                <span data-testid={`agreed-badge-${line.lineId}`}>
                  {t("orderPriceAdjustment.agreedBadge")}
                </span>
              </BadgeChip>
            )}
            <span className={"relative block w-full min-w-0 max-w-[12rem] sm:w-36"}>
              <input
                type="text"
                inputMode="decimal"
                value={raw}
                placeholder={t("orderPriceAdjustment.noAgreement")}
                disabled={disabled}
                onChange={(e) => onChange(e.target.value)}
                className={inputClass + stateClass}
                aria-invalid={invalid}
                aria-label={`${t("orderPriceAdjustment.agreedPrice")} ${line.displayNo}`}
                title={invalid ? t("orderPriceAdjustment.invalidInput") : undefined}
                data-testid={`agreed-price-${line.lineId}`}
                data-state={
                  invalid
                    ? "invalid"
                    : changed
                      ? "changed"
                      : showsStoredAgreement
                        ? "agreed"
                        : "none"
                }
              />
              <span
                className={
                  "pointer-events-none absolute inset-y-0 right-2 flex items-center text-xs text-gray-500"
                }
                aria-hidden="true"
              >
                {currency}
              </span>
            </span>
          </span>
        ) : (
          <span>&mdash;</span>
        )}
      </td>
      <td
        className={
          `${cellClass} sm:text-right tabular-nums` +
          (lineTotalChanged ? " font-semibold text-orange-700" : "")
        }
      >
        <span className={cellLabelClass}>
          {t("orderPriceAdjustment.lineTotal")}
        </span>
        <span>{formatMoney(lineTotal, currency)}</span>
      </td>
    </tr>
  );
}

// Before | After per total, and the difference (after - before) of the
// total underneath, in the orange of an unsaved change when it is not zero.
function TotalsCard({
  before,
  after,
  currency,
}: {
  before: OrderPriceAdjustmentDto["totals"];
  after: OrderPriceAdjustmentDto["totals"];
  currency: string;
}): React.ReactElement {
  const { t } = useTranslation();
  const rows: [string, number, number][] = [
    ["orderPriceAdjustment.subTotal", before.subTotal, after.subTotal],
    ["orderPriceAdjustment.vat", before.vat, after.vat],
    ["orderPriceAdjustment.total", before.total, after.total],
  ];
  const difference = round2(round2(after.total) - round2(before.total));
  const differenceText =
    difference === 0
      ? formatMoney(0, currency)
      : `${difference < 0 ? "-" : "+"}${formatMoney(Math.abs(difference), currency)}`;
  return (
    <div
      className={
        "w-full rounded-lg border border-[#ECEEF9] bg-[#FAFBFE] px-4 py-3 text-sm tabular-nums md:w-[22rem]"
      }
      data-testid="adjust-prices-totals"
    >
      <table className={"w-full"}>
        <thead className={"text-[11px] uppercase tracking-wide text-gray-500"}>
          <tr>
            <th></th>
            <th className={"pb-1 pl-2 text-right font-semibold"}>
              {t("orderPriceAdjustment.before")}
            </th>
            <th className={"pb-1 pl-2 text-right font-semibold"}>
              {t("orderPriceAdjustment.after")}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map(([key, b, a], i) => (
            <tr key={key} className={i === rows.length - 1 ? "font-bold" : ""}>
              <td className={"py-0.5 text-left text-gray-600"}>{t(key)}</td>
              <td className={"py-0.5 pl-2 text-right"}>
                {formatMoney(round2(b), currency)}
              </td>
              <td
                className={
                  "py-0.5 pl-2 text-right" +
                  (round2(a) !== round2(b) ? " text-orange-700" : "")
                }
              >
                {formatMoney(round2(a), currency)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div
        className={
          "mt-2 flex items-center justify-between border-t border-[#ECEEF9] pt-2 font-semibold"
        }
      >
        <span className={"text-gray-600"}>
          {t("orderPriceAdjustment.difference")}
        </span>
        <span
          className={difference === 0 ? "text-gray-500" : "text-orange-700"}
          data-testid="adjust-prices-difference"
        >
          {differenceText}
        </span>
      </div>
    </div>
  );
}

// Every saved version, compact: version, date, who, total before -> after
// and the note. Same responsive cells as the line table (a stacked card per
// version on a phone).
function HistoryTable({
  history,
  currency,
}: {
  history: OrderPriceAdjustmentHistoryDto[];
  currency: string;
}): React.ReactElement {
  const { t } = useTranslation();
  return (
    <section data-testid="adjust-prices-history">
      <p className={"mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500"}>
        {t("orderPriceAdjustment.historyTitle")}
      </p>
      <div className={"rounded-lg border border-[#ECEEF9]"}>
        <table className={"w-full table-fixed border-collapse text-xs"}>
          <thead className={"hidden bg-[#FAFBFE] sm:table-header-group"}>
            <tr>
              <th className={`${headClass} w-14 text-left`}>
                {t("orderPriceAdjustment.version")}
              </th>
              <th className={`${headClass} w-24 text-left`}>
                {t("orderPriceAdjustment.date")}
              </th>
              <th className={`${headClass} text-left`}>
                {t("orderPriceAdjustment.by")}
              </th>
              <th className={`${headClass} w-56 text-right`}>
                {t("orderPriceAdjustment.before")} &rarr;{" "}
                {t("orderPriceAdjustment.after")}
              </th>
              <th className={`${headClass} text-left`}>
                {t("orderPriceAdjustment.noteLabel")}
              </th>
            </tr>
          </thead>
          <tbody className={"divide-y divide-[#ECEEF9]"}>
            {history.map((h) => (
              <tr
                key={h.version}
                className={"block px-3 py-2 sm:table-row sm:p-0"}
                data-testid={`adjust-prices-history-${h.version}`}
              >
                <td className={`${cellClass} sm:text-left`}>
                  <span className={cellLabelClass}>
                    {t("orderPriceAdjustment.version")}
                  </span>
                  <span className={"font-semibold"}>v{h.version}</span>
                </td>
                <td className={`${cellClass} sm:text-left`}>
                  <span className={cellLabelClass}>
                    {t("orderPriceAdjustment.date")}
                  </span>
                  <span>{formatDate(new Date(parseUtcStamp(h.adjustedAtUtc)))}</span>
                </td>
                <td className={`${cellClass} sm:text-left`}>
                  <span className={cellLabelClass}>
                    {t("orderPriceAdjustment.by")}
                  </span>
                  <span className={"block min-w-0 truncate"} title={h.adjustedBy}>
                    {h.adjustedBy}
                  </span>
                </td>
                <td className={`${cellClass} sm:text-right tabular-nums whitespace-nowrap`}>
                  <span className={cellLabelClass}>
                    {t("orderPriceAdjustment.total")}
                  </span>
                  <span>
                    {formatMoney(h.totalBefore, currency)} &rarr;{" "}
                    <span className={"font-semibold"}>
                      {formatMoney(h.totalAfter, currency)}
                    </span>
                  </span>
                </td>
                <td className={`${cellClass} sm:text-left`}>
                  <span className={cellLabelClass}>
                    {t("orderPriceAdjustment.noteLabel")}
                  </span>
                  <span className={"text-gray-600"}>{h.note ?? ""}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
